import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  FileText,
  Loader2,
  CheckCircle2,
  X,
  ArrowLeft,
  Sparkles,
  AlertTriangle,
  Keyboard,
  FileUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils';
import { fetchChargeConceptsByModo } from '../../../parametrizacion/charge-concepts/Services/chargeConceptsService.js';
import AirCotizacionForm from './AirCotizacionForm.jsx';
import {
  extractAirCotizacion,
  createAirCotizacion,
  updateAirCotizacion,
  discardAirUpload,
  buildAirCotizacionPayload,
} from '../airCotizaciones.service.js';

const MAX_BYTES = 20 * 1024 * 1024;
const TIPOS_OK = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

const PASOS_ARCHIVO = [
  { id: 0, label: 'Cargar' },
  { id: 1, label: 'Procesar' },
  { id: 2, label: 'Revisar' },
  { id: 3, label: 'Guardar' },
];

// La carga manual no tiene nada que procesar: se pasa directo a la
// planilla.
const PASOS_MANUAL = [
  { id: 0, label: 'Origen' },
  { id: 2, label: 'Datos' },
  { id: 3, label: 'Guardar' },
];

/**
 * Asistente para cargar la cotización aérea que respondió un proveedor
 * (gemelo de `CargarLclCotizacionDialog`).
 *
 * Dos caminos, porque no todas las cotizaciones llegan como archivo:
 *
 *  - **Desde un archivo**: subir el PDF, interpretarlo, revisar lo que
 *    se entendió y guardar. La revisión no es opcional a propósito: la
 *    lectura automática se equivoca y una tarifa mal cargada se
 *    arrastra hasta la oferta al cliente.
 *  - **A mano**: cuando el proveedor contestó por correo, WhatsApp o
 *    teléfono. Se va directo a la planilla, ya prellenada con la ruta y
 *    la carga de la solicitud.
 *
 * En modo edición arranca directo en la planilla.
 */
export default function CargarAirCotizacionDialog({
  open,
  onOpenChange,
  airRequest,
  cotizacion = null,
  invitationId = null,
  onSaved,
}) {
  const toast = useToast();
  const esEdicion = !!cotizacion;

  // null hasta que el operador elige cómo va a cargarla.
  const [modo, setModo] = useState(esEdicion ? 'archivo' : null);
  const [paso, setPaso] = useState(esEdicion ? 2 : 0);
  const [archivo, setArchivo] = useState(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [etapa, setEtapa] = useState('');
  const [borrador, setBorrador] = useState(cotizacion || {});
  const [guardando, setGuardando] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const inputRef = useRef(null);
  // Para poder limpiar el archivo de R2 si el operador se arrepiente.
  const r2KeyRef = useRef(null);
  const guardadoRef = useRef(false);

  const magnitudes = {
    piezas: airRequest?.numPiezas,
    pesoBrutoKg: airRequest?.pesoTotal,
    pesoVolumetricoKg: airRequest?.pesoVolumetrico,
    pesoCobrableKg: airRequest?.pesoCobrable,
    volumenM3: airRequest?.volumenTotal,
    valorMercancia: airRequest?.valorMercancia,
    bls: 1,
    documentos: 1,
    contenedores: 1,
  };

  useEffect(() => {
    if (!open) return;
    setPaso(esEdicion ? 2 : 0);
    setModo(esEdicion ? 'archivo' : null);
    setArchivo(null);
    setProgreso(0);
    setEtapa('');
    setBorrador(cotizacion || {});
    guardadoRef.current = false;
    r2KeyRef.current = null;
  }, [open, cotizacion, esEdicion]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await fetchChargeConceptsByModo('aereo');
        if (r?.success) setCatalogo(r.data || []);
      } catch {
        /* sin catálogo el combobox queda vacío, pero se puede seguir */
      }
    })();
  }, [open]);

  /** Si se cierra sin guardar, el archivo subido queda huérfano en R2. */
  const cerrar = useCallback(
    (abierto) => {
      if (!abierto && !guardadoRef.current && r2KeyRef.current) {
        discardAirUpload(r2KeyRef.current);
        r2KeyRef.current = null;
      }
      onOpenChange?.(abierto);
    },
    [onOpenChange],
  );

  const validarArchivo = (f) => {
    if (!f) return 'No se seleccionó ningún archivo';
    if (f.size > MAX_BYTES) return 'El archivo supera los 20 MB';
    if (!TIPOS_OK.includes(f.type) && !/\.(pdf|docx)$/i.test(f.name)) {
      return 'Formato no soportado. Se aceptan PDF y DOCX.';
    }
    return null;
  };

  const elegirArchivo = (f) => {
    const err = validarArchivo(f);
    if (err) {
      toast.error('Archivo no válido', err);
      return;
    }
    setArchivo(f);
  };

  /**
   * Borrador para la carga a mano.
   *
   * Se prellena con la ruta, el incoterm y la carga de la solicitud: son
   * datos que el operador ya cargó una vez y que además hacen falta para
   * que las bases por kilo y por peso cobrable puedan calcular algo.
   */
  const empezarManual = () => {
    const r = airRequest || {};
    const aeropuerto = (snap) =>
      snap
        ? {
            id: snap._id || snap.id || null,
            iata: snap.iata || '',
            nombre: snap.nombre || '',
            ciudad: snap.ciudad || '',
            pais: snap.pais || '',
          }
        : null;

    setBorrador({
      numero: '',
      fecha: new Date().toISOString().slice(0, 10),
      moneda: 'USD',
      origenExtraccion: 'manual',
      procesamientoParcial: false,
      conceptos: [],

      origen: aeropuerto(r.aeropuertoCargaSnapshot),
      destino: aeropuerto(r.aeropuertoDescargaSnapshot),
      incotermId: r.incotermId || r.incotermSnapshot?.id || r.incotermSnapshot?._id || null,
      incotermSnapshot: r.incotermSnapshot
        ? {
            id: r.incotermSnapshot._id || r.incotermSnapshot.id || null,
            codigo: r.incotermSnapshot.codigo || '',
            nombre: r.incotermSnapshot.nombre || '',
          }
        : null,

      numeroPiezas: r.numPiezas ?? null,
      pesoBrutoKg: r.pesoTotal ?? null,
      pesoVolumetricoKg: r.pesoVolumetrico ?? null,
      pesoCobrableKg: r.pesoCobrable ?? null,
      volumenM3: r.volumenTotal ?? null,
      valorMercancia: r.valorMercancia ?? null,
      monedaValorMercancia: r.monedaValorMercancia || 'USD',
      descripcionMercancia: r.descripcion || '',
    });
    setModo('manual');
    setPaso(2);
  };

  const procesar = async () => {
    if (!archivo) return;
    setPaso(1);
    setProgreso(0);
    setEtapa('Subiendo el documento…');
    try {
      const res = await extractAirCotizacion({
        airRequestId: airRequest._id,
        file: archivo,
        invitationId,
        onProgress: (p) => {
          setProgreso(p);
          if (p >= 100) setEtapa('Interpretando el documento…');
        },
      });
      const n = res?.normalized || {};
      r2KeyRef.current = n?._documento?.r2Key || null;
      setBorrador(n);
      setPaso(2);
    } catch (e) {
      toast.error('No se pudo procesar el documento', e?.message);
      setPaso(0);
    }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const payload = buildAirCotizacionPayload({
        ...borrador,
        airRequestId: airRequest._id,
      });
      const r = esEdicion
        ? await updateAirCotizacion(cotizacion._id, (() => {
            const { airRequestId, documento, ...resto } = payload;
            return resto;
          })())
        : await createAirCotizacion(payload);

      if (!r?.success) {
        const msg = [].concat(r?.message || 'Error desconocido').join(' · ');
        toast.error('No se pudo guardar', msg);
        return;
      }
      guardadoRef.current = true;
      setPaso(3);
      toast.success(
        esEdicion ? 'Cotización actualizada' : 'Cotización guardada',
        r.data?.numero,
      );
      onSaved?.(r.data);
    } catch (e) {
      toast.error('No se pudo guardar', e?.message);
    } finally {
      setGuardando(false);
    }
  };

  if (!open) return null;

  const conceptos = borrador?.conceptos || [];
  const esManual = modo === 'manual';
  const pasos = esManual ? PASOS_MANUAL : PASOS_ARCHIVO;

  return (
    <Dialog open={open} onOpenChange={cerrar}>
      {/*
        Usa el `Dialog` del proyecto, no un overlay propio: este modal se
        abre desde adentro del detalle de la solicitud, que es un
        `DialogContent` de Radix con `transform`. Un ancestro transformado
        pasa a ser el bloque contenedor de los `position: fixed` de adentro,
        así que `inset-0` medía contra esa tarjeta en vez de contra la
        pantalla y el `overflow-hidden` del padre recortaba encabezado y pie.
      */}
      <DialogContent
        hideClose
        className="flex h-[calc(100vh-2rem)] w-full max-w-5xl flex-col gap-0 overflow-hidden p-0"
      >
        {/* Encabezado */}
        <div className="flex shrink-0 items-start justify-between border-b p-4">
          <div>
            <h3 className="text-lg font-semibold">
              {esEdicion ? 'Editar cotización' : 'Cargar cotización de proveedor'}
            </h3>
            <p className="text-sm text-muted-foreground">
              Solicitud {airRequest?.codigo}
              {airRequest?.clienteSnapshot?.nombre
                ? ` · ${airRequest.clienteSnapshot.nombre}`
                : ''}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={() => cerrar(false)}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Pasos */}
        <div className="flex shrink-0 items-center gap-2 border-b px-4 py-3">
          {pasos.map((p, i) => (
            <React.Fragment key={p.id}>
              <div className="flex items-center gap-1.5">
                <span
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium',
                    paso === p.id
                      ? 'bg-primary text-primary-foreground'
                      : paso > p.id
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-muted text-muted-foreground',
                  )}
                >
                  {paso > p.id ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span
                  className={cn(
                    'text-xs',
                    paso === p.id ? 'font-medium' : 'text-muted-foreground',
                  )}
                >
                  {p.label}
                </span>
              </div>
              {i < pasos.length - 1 && <div className="h-px flex-1 bg-border" />}
            </React.Fragment>
          ))}
        </div>

        {/* Contenido */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <AnimatePresence mode="wait">
            {paso === 0 && modo === null && (
              <motion.div
                key="p0-elegir"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid grid-cols-1 gap-4 py-4 md:grid-cols-2"
              >
                <button
                  type="button"
                  onClick={() => setModo('archivo')}
                  className="flex flex-col items-center rounded-lg border-2 border-dashed p-8 text-center transition-colors hover:border-primary hover:bg-primary/5"
                >
                  <FileUp className="mb-3 h-10 w-10 text-primary" />
                  <p className="font-medium">Desde un archivo</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    El proveedor mandó un PDF o un DOCX. Se sube y se
                    interpreta solo; después lo revisas.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={empezarManual}
                  className="flex flex-col items-center rounded-lg border-2 border-dashed p-8 text-center transition-colors hover:border-primary hover:bg-primary/5"
                >
                  <Keyboard className="mb-3 h-10 w-10 text-primary" />
                  <p className="font-medium">Cargarla a mano</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Contestó por correo, WhatsApp o teléfono. Se abre la
                    planilla con la ruta y la carga ya puestas.
                  </p>
                </button>
              </motion.div>
            )}

            {paso === 0 && modo === 'archivo' && (
              <motion.div key="p0" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setArrastrando(true);
                  }}
                  onDragLeave={() => setArrastrando(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setArrastrando(false);
                    elegirArchivo(e.dataTransfer.files?.[0]);
                  }}
                  className={cn(
                    'flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-10 text-center transition-colors',
                    arrastrando ? 'border-primary bg-primary/5' : 'border-muted-foreground/25',
                  )}
                >
                  <Upload className="mb-3 h-10 w-10 text-muted-foreground" />
                  <p className="font-medium">
                    Arrastra el PDF que mandó el proveedor
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    o haz clic para buscarlo · PDF o DOCX · hasta 20 MB
                  </p>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".pdf,.docx,application/pdf"
                    className="hidden"
                    onChange={(e) => elegirArchivo(e.target.files?.[0])}
                  />
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => inputRef.current?.click()}
                  >
                    Seleccionar archivo
                  </Button>

                  {archivo && (
                    <div className="mt-4 flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{archivo.name}</span>
                      <Badge variant="secondary" className="font-normal">
                        {(archivo.size / 1024 / 1024).toFixed(1)} MB
                      </Badge>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {paso === 1 && (
              <motion.div
                key="p1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-16"
              >
                <Loader2 className="mb-4 h-10 w-10 animate-spin text-primary" />
                <p className="font-medium">{etapa}</p>
                {progreso > 0 && progreso < 100 && (
                  <div className="mt-4 h-2 w-64 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${progreso}%` }}
                    />
                  </div>
                )}
                <p className="mt-3 text-xs text-muted-foreground">
                  Leer un PDF con IA puede tardar hasta un minuto.
                </p>
              </motion.div>
            )}

            {paso === 2 && (
              <motion.div key="p2" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <AirCotizacionForm
                  data={borrador}
                  onChange={setBorrador}
                  magnitudes={magnitudes}
                  catalogo={catalogo}
                />
              </motion.div>
            )}

            {paso === 3 && (
              <motion.div
                key="p3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-16 text-center"
              >
                <CheckCircle2 className="mb-4 h-12 w-12 text-emerald-600" />
                <p className="text-lg font-medium">
                  {esEdicion ? 'Cotización actualizada' : 'Cotización guardada'}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {borrador.numero} · {conceptos.length} concepto(s)
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Pie */}
        <div className="flex shrink-0 items-center justify-between border-t p-4">
          <div className="text-xs text-muted-foreground">
            {paso === 2 && conceptos.length === 0 && (
              <span className="flex items-center gap-1 text-amber-700">
                <AlertTriangle className="h-3 w-3" />
                Sin conceptos: agrégalos antes de guardar o quedará solo la cabecera.
              </span>
            )}
            {paso === 2 && conceptos.length > 0 && (
              <span className="flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                {conceptos.filter((c) => c.conceptoCodigo).length} de {conceptos.length}{' '}
                conceptos con código del catálogo
              </span>
            )}
            {paso === 2 && esManual && conceptos.length === 0 && (
              <span className="flex items-center gap-1 text-amber-700">
                <AlertTriangle className="h-3 w-3" />
                Agrega los conceptos que te pasó el proveedor.
              </span>
            )}
          </div>

          <div className="flex gap-2">
            {paso === 2 && !esEdicion && (
              <Button
                variant="outline"
                onClick={() => {
                  setPaso(0);
                  setModo(null);
                  setArchivo(null);
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Volver
              </Button>
            )}
            {paso === 0 && modo === 'archivo' && (
              <>
                <Button variant="outline" onClick={() => setModo(null)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Volver
                </Button>
                <Button disabled={!archivo} onClick={procesar}>
                  Procesar documento
                </Button>
              </>
            )}
            {paso === 2 && (
              <Button onClick={guardar} disabled={guardando}>
                {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {esEdicion ? 'Guardar cambios' : 'Guardar cotización'}
              </Button>
            )}
            {paso === 3 && <Button onClick={() => cerrar(false)}>Cerrar</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
